import { Component, EventEmitter, Inject, Injector, Input, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpParams } from "@angular/common/http";
import { ToastrService } from 'ngx-toastr';

import { CellClickEvent, ColumnMenuSettings, GridDataResult, GridItem, SelectableSettings, DetailTemplateShowIfFn } from "@progress/kendo-angular-grid";
import { CompositeFilterDescriptor, GroupDescriptor, State, distinct, toDataSourceRequestString, translateDataSourceResultGroups, groupBy, process, translateAggregateResults } from "@progress/kendo-data-query";
import {
    SVGIcon, fileExcelIcon, filePdfIcon, saveIcon, checkboxNullIcon, checkIcon, radiobuttonCheckedIcon, radiobuttonIcon, plusIcon,
    pencilIcon, tableIcon, minusOutlineIcon, checkOutlineIcon, colResizeIcon
} from "@progress/kendo-svg-icons";
import { DialogService } from "@progress/kendo-angular-dialog";
import { IntlService } from "@progress/kendo-angular-intl";

import { ClientDataService } from "../../services/client-data.service";
import { GridConfig } from "../common/api/grid-config.interface";
import { PrimeNGConfig } from "primeng/api";
import { menuAnimation, stateAnimation } from "../../api/animations";
import { ConfirmationDialogResult, ConfirmationDialogService } from "../../services/confirmation-dialog.service";
import { stringFormat } from "../../helpers/string-format";
import { cleanFilters, describeFilter } from "../../helpers/clean-filters";
import { BaseGridComponent } from '../common/api/base-grid.component';
import { GRID_TOKEN } from '../common/api/grid-token';
import { mapDateFilter } from "../common/api/map-date-fliter";
import { isoDateStringToDate, isIsoDateString } from "../../helpers/convert-date-properties";
import { BASE_URL } from "@jwt";

const closest = (node, predicate) => {
    while (node && !predicate(node)) {
        node = node.parentNode;
    }

    return node;
};

const content = node => {
    return node.classList?.contains('k-grid-content');
};

@Component({
    selector: "remote-kendo-grid",
    templateUrl: "./grid.component.html",
    providers: [{ provide: GRID_TOKEN, useExisting: RemoteKendoGridComponent }],
    animations: [stateAnimation, menuAnimation]
})
export class RemoteKendoGridComponent extends BaseGridComponent implements OnInit {
    @ViewChild("dialogContainer", { read: ViewContainerRef }) public dialogContainerRef: ViewContainerRef;

    filePdfIcon: SVGIcon = filePdfIcon;
    fileExcelIcon: SVGIcon = fileExcelIcon;
    saveIcon: SVGIcon = saveIcon;
    plusIcon: SVGIcon = plusIcon;
    checkboxNullIcon: SVGIcon = checkboxNullIcon;
    checkIcon: SVGIcon = checkIcon;
    radiobuttonCheckedIcon: SVGIcon = radiobuttonCheckedIcon;
    radiobuttonIcon: SVGIcon = radiobuttonIcon;
    pencilIcon: SVGIcon = pencilIcon;
    tableIcon: SVGIcon = tableIcon;
    checkOutlineIcon: SVGIcon = checkOutlineIcon;
    minusOutlineIcon: SVGIcon = minusOutlineIcon;
    colResizeIcon: SVGIcon = colResizeIcon;
    autofitColumnsLabel: string;
    trueLabel: string;
    falseLabel: string;

    @Input() detailTemplate!: TemplateRef<any>;
    @Input() buttonTemplates!: { [key: string]: TemplateRef<any> }; // Dynamic templates
    @Input() animate: boolean = true;
    @Input() pathUrl: string;
    @Input() persistStatePopupEnable: boolean = false;
    @Input() dataKey: string = 'id';
    @Input() height: number;
    @Input() dblClickEnable: boolean = true;
    @Input() quickSearchEnable: boolean = true;
    @Input() checkboxColumnEnable: boolean = true;
    @Input() showSelectAllEnable: boolean = true;
    @Input() insertCommandEnable: boolean = true;
    @Input() deleteCommandEnable: boolean = true;
    @Input() filterCommandEnable: boolean = true;
    @Input() excelCommandEnable: boolean = true;
    @Input() pdfCommandEnable: boolean = true;
    @Input() columnChooserCommandEnable: boolean = true;
    @Input() resizable: boolean = true;
    @Input() reorderable: boolean = true;
    @Input() columnMenu: ColumnMenuSettings | boolean = false;
    @Input() selectable: SelectableSettings | boolean = {
        enabled: true,
        checkboxOnly: false,
        mode: `multiple`,
        cell: false,
        drag: false
    };
    @Input() postModel: any = {};

    @Input() getApiUrl: string;
    @Input() postApiUrl: string;
    @Input() deleteApiUrl: string;
    @Input() deleteSelectedApiUrl: string;
    @Input() settingsMenuEnable: boolean;
    settingsMenuData: any[];
    settingsMenuLabel: string;
    @Input() gridMenuEnable: boolean = true;
    @Input() persistStateMenuEnable: boolean = true;

    @Input() customMenuEnable: boolean = false;
    @Input() customMenuData: any[];
    @Input() customMenuLabel: string;
    @Input() detailTemplateShowIf: DetailTemplateShowIfFn = () => { return true; };

    emptyFilter: CompositeFilterDescriptor = { filters: [], logic: "or" };
    filterValue: CompositeFilterDescriptor;
    newFilterValue: CompositeFilterDescriptor;
    filterPanelEnable: boolean = false;

    gridData: GridDataResult;

    insertLabel: string;
    deleteLabel: string;
    searchLabel: string;
    applyLabel: string;
    private quickSearch: string;
    private cellClickEvent: CellClickEvent;
    @Output() public onColumnsChange: EventEmitter<any[]> = new EventEmitter();

    public trackByItem(index: number, item: GridItem): any {
        return index;
        //return item.data[this.config.dataKey];
    }

    public groupHeaderTemplate(value: any) {
        if (typeof value === 'boolean')
            return value ? this.trueLabel : this.falseLabel;
        if (typeof value === 'string' && isIsoDateString(value)) {
            return this.intlService.formatDate(new Date(value), 'd');
        }

        return value;
    }

    get toolbalSeparatorEnable() {
        return this.insertCommandEnable || this.deleteCommandEnable;
    }
    getOperator(field: string) {
        return this.aggregateDescriptors?.find(a => a.field === field)?.aggregate
    }
    constructor(
        primengConfig: PrimeNGConfig,
        toastrService: ToastrService,
        dialogService: DialogService,
        @Inject(BASE_URL) private baseUrl: string,
        private dataService: ClientDataService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private intlService: IntlService,
        private confirmService: ConfirmationDialogService) {
        super(primengConfig, toastrService, dialogService);
    }

    ngOnInit(): void {
        this.trueLabel = this.primengConfig.getTranslation('aria')['trueLabel'];
        this.falseLabel = this.primengConfig.getTranslation('aria')['falseLabel'];
        this.insertLabel = this.primengConfig.getTranslation('common.insert');
        this.deleteLabel = this.primengConfig.getTranslation('common.delete');
        this.searchLabel = this.primengConfig.getTranslation('common.search');
        this.autofitColumnsLabel = this.primengConfig.getTranslation('grid.autofitCulumns');
        this.settingsMenuLabel = this.primengConfig.getTranslation('common.settings');
        this.applyLabel = this.primengConfig.getTranslation('common.apply');

        const settingsMenuData: any[] = [];

        if (this.gridMenuEnable) {
            const gridMenuData: any = [
                { text: this.primengConfig.getTranslation('common.configuration'), disabled: true },
                { text: this.primengConfig.getTranslation('grid.showGroup'), click: () => { this.groupable.enabled = !this.groupable.enabled; } },
                //{ text: this.primengConfig.getTranslation('grid.showFooter'), click: () => { this.groupable.showFooter = !this.groupable.showFooter; } }
            ];

            settingsMenuData.push(...gridMenuData);
        }

        if (this.persistStateMenuEnable) {
            const persistStateMenuData: any = [
                { text: this.primengConfig.getTranslation('grid.views'), disabled: true },
                { text: this.primengConfig.getTranslation('title.newView'), svgIcon: saveIcon, click: () => { this.openPersistModelDialog(); } },
                { text: this.primengConfig.getTranslation('title.manageViews'), svgIcon: pencilIcon, click: () => { this.openPersistListDialog(); } }
            ];

            settingsMenuData.push(...persistStateMenuData);
        }

        if (settingsMenuData.length > 0) {
            this.settingsMenuEnable = true;
            this.settingsMenuData = settingsMenuData;
        }

        this.loadData();
    }

    public loadData(): void {
        const url = `${this.baseUrl}/${this.getApiUrl}`;

        this.loading = true;
        this.dataService.fetchJsonBodyGet(url)
            .then((helperModel: any) => {
                this.helperModel = helperModel;
                this.pageTitle = helperModel.customProperties.title;
                this.mapGridConfig();
                this.getDataSource();
            })
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

    public getDataSource(): Promise<void> {
        if (this.aggregatable && this.aggregateDescriptors.length > 0)
            this.state["aggregates"] = this.aggregateDescriptors;

        const url = `${this.baseUrl}/${this.postApiUrl}?${toDataSourceRequestString(this.state)}`;
        const hasGroups = this.state.group && this.state.group.length;

        this.loading = true;
        return this.dataService.fetchJsonBodyPost<GridDataResult>(url, this.postModel, { quickSearch: this.quickSearch })
            .then(({ data, total/*, aggregateResults*/ }) => {
                const gridData = {
                    // If the data is grouped, translate it to a compatible format using the translateDataSourceResultGroups helper.
                    data: hasGroups ? translateDataSourceResultGroups(data) : data,
                    total: total,
                    //aggregateResult: translateAggregateResults(aggregateResults)
                };
                this.gridData = {
                    data: gridData.data,
                    total: gridData.total
                };
            })
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

    // Next version
    //get filterDiscription(): string {
    //    return this.newFilterValue && this.filterValue?.filters ? describeFilter(this.newFilterValue) : undefined;
    //}

    onFilterChange(value: CompositeFilterDescriptor): void {
        this.newFilterValue = { ...value };
    }

    toggleFilterPanel() {
        this.filterPanelEnable = !this.filterPanelEnable;
        this.filterValue = this.filterPanelEnable ? { ...this.state.filter } : this.emptyFilter;
    }

    applyFilter() {
        this.filterPanelEnable = false;
        this.filterValue = this.emptyFilter;

        setTimeout(() => {
            this.state.filter = this.newFilterValue = cleanFilters(this.newFilterValue);
            this.getDataSource();
        }, 100);
    }

    getHttpParamsFromRouter(): HttpParams {
        let params = new HttpParams();

        Object.keys(this.activatedRoute.params).forEach((key) => {
            if (this.activatedRoute.params[key] !== undefined) {
                params = params.set(key, this.activatedRoute.params[key]);
            }
        });

        return params;
    }

    public dataStateChange(state: State): void {
        this.state = state;
        this.getDataSource();
    }

    public onReorder(e: any): void {
        const reorderedColumn = this.columns.splice(e.oldIndex, 1);
        this.columns.splice(e.newIndex, 0, ...reorderedColumn);
    }

    public onResize(e: any): void {
        e.forEach((item) => {
            this.columns.find(
                (col) => col.field === item.column.field
            ).width = item.newWidth;
        });
    }

    public onVisibilityChange(e: any): void {
        e.columns.forEach((column) => {
            this.columns.find(
                (col) => col.field === column.field
            ).hidden = column.hidden;
        });
    }

    public mapGridConfig() {
        const config = JSON.parse(this.helperModel.token) as GridConfig;

        mapDateFilter(config.state.filter, config.columns);

        this.state = config.state;

        this.onColumnsChange.emit(config.columns);
        this.columns = config.columns.sort(
            (a: any, b: any) => a.orderIndex - b.orderIndex
        );
        this.pager = config.pager;
        this.groupable = config.groupable;

        this.filterExpressions = config.filterExpressions;
    }

    public insertCommand() {
        this.router.navigate([this.pathUrl, 0]);
    }

    public deleteCommand() {
        const title = this.primengConfig.getTranslation('common.confirmation');
        const content = stringFormat(this.primengConfig.getTranslation('message.deleteSelection'), this.selectedKeys.length);

        this.confirmService.openDialog(title, content, false)
            .then((result: ConfirmationDialogResult) => {
                if (result == ConfirmationDialogResult.Ok) {
                    if (this.deleteSelectedApiUrl && this.selectedKeys.length > 1) {
                        this.deleteEntityCore(`${this.baseUrl}/${this.deleteSelectedApiUrl}`, this.selectedKeys);
                    } else {
                        this.deleteEntityCore(`${this.baseUrl}/${this.deleteApiUrl}`, this.selectedKeys[0]);
                    }
                }
            });
    }

    private deleteEntityCore(apiUrl: string, value: number | Array<number>): void {

        const { url, body, params } = Array.isArray(value)
            ? { url: apiUrl, body: this.selectedKeys, params: undefined }
            : { url: apiUrl, body: {}, params: { id: this.selectedKeys[0] } };

        this.loading = true;
        this.dataService.fetchJsonBodyPost(url, body, params)
            .then((result: any) => {
                this.getDataSource().then(() => {
                    this.clearSelectedKeys();
                    this.toastrService.success(result?.info || this.primengConfig.getTranslation('message.deletionCompleted'));
                });
            })
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

    // DblClick
    public onCellClick(event: CellClickEvent) {
        if (event.type == 'click')
            this.cellClickEvent = event;
    }

    public onDblClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const exist = target.closest('.k-grid-checkbox-column') || target.closest('.k-button');

        if (this.dblClickEnable && this.cellClickEvent && !exist) {

            if (closest(event.target as HTMLElement, content)) {
                this.router.navigate([this.pathUrl, this.cellClickEvent.dataItem[this.dataKey]]);
            }
        }

    }

    // QuickSearch
    public onQuickSearchClick(ev: MouseEvent): void {
        ev.stopImmediatePropagation();
    }

    public onQuickSearchKeydown(ev: KeyboardEvent, wrapper: HTMLDivElement): void {
        if (ev.key === "Escape") {
            wrapper.focus();
        }
        if (ev.key === "ArrowLeft" || ev.key === "ArrowRight") {
            ev.stopImmediatePropagation();
        }
    }

    public onQuickSearchValueChange(value: any) {
        this.quickSearch = value;

        // Refresh the data
        this.getDataSource();
    }

    //
    distinctPrimitive(fieldName: string): unknown[] {
        return distinct(this.gridData.data, fieldName).map((item) => item[fieldName]);
    }
}
